import React, { useState } from "react";
import { RefundPolicySection } from "./styles";
import { Box, Container, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

type Lang = "EN" | "AR";

const RefundPolicy = () => {
	const [lang, setLang] = useState<Lang>("AR");
	const isAr = lang === "AR";

	return (
		<Box pt={4}>
			<RefundPolicySection dir={isAr ? "rtl" : "ltr"}>
				<Container maxWidth={"md"}>
					<Box display={"flex"} justifyContent={"flex-end"} mb={2}>
						<ToggleButtonGroup
							size={"small"}
							value={lang}
							exclusive
							onChange={(_, value) => value && setLang(value)}
							aria-label={"language toggle"}
							style={{ direction: "ltr" }}
						>
							<ToggleButton value={"EN"}>EN</ToggleButton>
							<ToggleButton value={"AR"}>AR</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box display={"flex"} flexDirection={"column"}>
						<Typography variant={"h2"} className={"page-title"}>
							{isAr ? "سياسة الاسترداد والإلغاء" : "Refund & Cancellation Policy"}
						</Typography>
						<Typography className={"effective-date"}>
							{isAr ? "تاريخ السريان: 14 يونيو 2026" : "Effective Date: June 14, 2026"}
						</Typography>

						<Typography className={"policy-intro"}>
							{isAr
								? "تحكم هذه السياسة جميع عمليات الشراء التي تتم عبر منصة VitesLink. نظرًا لأن خدمتنا رقمية وتُسلَّم وتُتاح فور التفعيل، فإن الاسترداد يقتصر بشكل صارم على الحالات الموضحة أدناه. بإتمامك عملية الشراء، فإنك توافق على شروط هذه السياسة."
								: "This policy governs all purchases made on the VitesLink platform. Because our product is a digital service that is delivered and accessible immediately upon activation, refunds are strictly limited to the circumstances described below. By completing a purchase, you agree to the terms of this policy."}
						</Typography>

						<Divider sx={{ mb: 4 }} />

						{/* Section 1 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "1. أهلية الاسترداد" : "1. Refund Eligibility"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										لن يُنظر في طلب الاسترداد إلا عند توفر <strong>جميع</strong> الشروط التالية وقت تقديم الطلب:
									</>
								) : (
									<>
										A refund request will only be considered when <strong>all</strong> of the following conditions are
										satisfied at the time of the request:
									</>
								)}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>
											يتم تقديم الطلب خلال <strong>48 ساعة</strong> من تاريخ الدفع الأصلي.
										</li>
										<li>
											لم يتم نشر الدعوة بعد، <strong>ولم</strong> يتم إنشاء أي رابط قابل للمشاركة.
										</li>
										<li>
											لا تزال الدعوة في حالة <strong>مسودة</strong>.
										</li>
										<li>لم يقم أي ضيف بالدخول إلى الدعوة أو مشاهدتها.</li>
										<li>لم يتم استخدام أو تفعيل ميزة تأكيد الحضور (RSVP) بأي شكل.</li>
									</>
								) : (
									<>
										<li>
											The request is submitted within <strong>48 hours</strong> of the original payment date.
										</li>
										<li>
											The invitation has <strong>not</strong> been published and no shareable link has been generated.
										</li>
										<li>
											The invitation remains in <strong>Draft</strong> status.
										</li>
										<li>No guests have accessed or viewed the invitation.</li>
										<li>RSVP functionality has not been used or triggered in any way.</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "بصرف النظر عما سبق، سيُنظر في طلب الاسترداد أيضًا في الحالات التالية:"
									: "Regardless of the above, a refund will also be considered for:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>خصم مكرر أو غير مقصود تم التحقق منه لنفس الدعوة.</li>
										<li>عطل تقني مؤكد ناتج عن منصة VitesLink حال دون وصولك إلى الخدمة أو استخدامها.</li>
										<li>مشكلة مؤكدة من جانب المنصة حالت دون إتمام الطلب.</li>
									</>
								) : (
									<>
										<li>A verified duplicate or accidental charge for the same invitation.</li>
										<li>
											A verified technical failure caused by VitesLink's platform that prevented you from accessing or
											using the service.
										</li>
										<li>A confirmed platform-side issue that prevented order fulfillment.</li>
									</>
								)}
							</ul>
						</Box>

						{/* Section 2 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "2. الحالات غير القابلة للاسترداد" : "2. Non-Refundable Situations"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										لا يتوفر الاسترداد بشكل عام <strong>بعد</strong> نشر الدعوة.
									</>
								) : (
									<>
										Refunds are generally <strong>not</strong> available after an invitation has been published.
									</>
								)}
							</Typography>{" "}
							<br />
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										لن يتم إصدار الاسترداد <strong>في</strong> الحالات التالية:
									</>
								) : (
									<>
										Refunds will <strong>not</strong> be issued under the following circumstances:
									</>
								)}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>تم نشر الدعوة أو إنشاء رابط المشاركة.</li>
										<li>تم فتح رابط الدعوة أو الدخول إليه من قبل أي ضيف.</li>
										<li>تم تسجيل أي ردود على تأكيد الحضور (RSVP).</li>
										<li>تمت مشاركة الدعوة عبر أي قناة (واتساب، وسائل التواصل الاجتماعي، البريد الإلكتروني، إلخ).</li>
										<li>مرت أكثر من 48 ساعة منذ تاريخ الدفع.</li>
										<li>يستند الطلب إلى تغيير الرأي أو التفضيل أو الظروف الشخصية بعد التفعيل.</li>
										<li>تم إجراء تعديلات على التصميم أو المحتوى أو أعمال تخصيص بعد الشراء.</li>
										<li>
											يدّعي المستخدم عدم استخدام الدعوة دون تقديم دليل يمكن التحقق منه على أنها لم تُنشر أو يُدخل إليها
											قط.
										</li>
										<li>تم حذف الدعوة من قبل المستخدم بعد نشرها.</li>
									</>
								) : (
									<>
										<li>The invitation has been published or the shareable link has been generated.</li>
										<li>The invitation link has been accessed or opened by any guest.</li>
										<li>Any RSVP responses have been recorded.</li>
										<li>The invitation has been shared via any channel (WhatsApp, social media, email, etc.).</li>
										<li>More than 48 hours have passed since the payment date.</li>
										<li>
											The request is based on a change of mind, preference, or personal circumstances after activation.
										</li>
										<li>Design edits, content changes, or customisation work have been performed after purchase.</li>
										<li>
											The user claims non-use without verifiable evidence that the invitation was never published or
											accessed.
										</li>
										<li>The invitation was deleted by the user after being published.</li>
									</>
								)}
							</ul>
							<Box className={"highlight-box"} mt={2}>
								<p>
									{isAr ? (
										<>
											<strong>مهم:</strong> بمجرد نشر الدعوة وتفعيل الرابط، تُعتبر الخدمة قد سُلِّمت بالكامل. لا يمكن
											إصدار أي استرداد بعد هذه المرحلة، بصرف النظر عن أي استخدام لاحق.
										</>
									) : (
										<>
											<strong>Important:</strong> Once an invitation is published and the link is live, the service is
											considered fully delivered. No refund can be issued after this point, regardless of subsequent
											use.
										</>
									)}
								</p>
							</Box>
						</Box>

						{/* Section 3 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "3. الإلغاء" : "3. Cancellation"}</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "يمكنك إلغاء دعوتك في أي وقت من خلال حسابك. إلا أن الإلغاء وحده لا يُعد سببًا كافيًا للاسترداد:"
									: "You may cancel your invitation at any time through your account. However, cancellation alone does not constitute grounds for a refund:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>
											<strong>قبل النشر، وخلال 48 ساعة من الدفع:</strong> يحق لك تقديم طلب استرداد وفق القسم الأول.
										</li>
										<li>
											<strong>بعد النشر أو بعد مرور 48 ساعة:</strong> تصبح عملية الشراء غير قابلة للاسترداد بصرف النظر
											عن الإلغاء.
										</li>
									</>
								) : (
									<>
										<li>
											<strong>Before publishing, within 48 hours of payment:</strong> eligible to submit a refund
											request per Section 1.
										</li>
										<li>
											<strong>After publishing or after 48 hours:</strong> the purchase is non-refundable regardless of
											cancellation.
										</li>
									</>
								)}
							</ul>
						</Box>

						{/* Section 4 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "4. كيفية طلب الاسترداد" : "4. How to Request a Refund"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										لتقديم طلب استرداد، راسلنا عبر البريد الإلكتروني{" "}
										<a href="mailto:info@viteslink.com">info@viteslink.com</a> وضع في عنوان الرسالة{" "}
										<strong>طلب استرداد – [رقم الدعوة الخاصة بك]</strong>، مع تضمين:
									</>
								) : (
									<>
										To submit a refund request, email us at <a href="mailto:info@viteslink.com">info@viteslink.com</a>{" "}
										with the subject line <strong>Refund Request – [Your Invitation ID]</strong> and include:
									</>
								)}
							</Typography>
							<ol>
								{isAr ? (
									<>
										<li>بريدك الإلكتروني المسجل.</li>
										<li>رقم تعريف الدعوة (يظهر في صفحة إدارة الدعوة / الإعدادات).</li>
										<li>رقم مرجع الدفع أو رقم المعاملة.</li>
										<li>وصف واضح لسبب طلبك.</li>
									</>
								) : (
									<>
										<li>Your registered email address.</li>
										<li>Your invitation ID (visible in the invitation management / settings page).</li>
										<li>Your payment reference or transaction ID.</li>
										<li>A clear description of the reason for your request.</li>
									</>
								)}
							</ol>
							<Typography className={"section-body"} mt={2}>
								{isAr ? (
									<>
										تتم مراجعة جميع الطلبات خلال <strong>3-5 أيام عمل</strong>. نحتفظ بحق طلب تحقق إضافي قبل معالجة أي
										استرداد. تُعاد المبالغ المستردة المعتمدة إلى وسيلة الدفع الأصلية.
									</>
								) : (
									<>
										All requests are reviewed within <strong>3–5 business days</strong>. We reserve the right to request
										additional verification before processing any refund. Approved refunds are returned to the original
										payment method.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 5 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "5. لا استرداد جزئي" : "5. No Partial Refunds"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "يتم إصدار الاسترداد للمبلغ الكامل فقط. لا تتوفر أي استردادات جزئية لأي سبب."
									: "Refunds are issued for the full purchase amount only. No partial refunds are available for any reason."}
							</Typography>
						</Box>

						{/* Section 6 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "6. النزاعات المصرفية وخلافات الدفع" : "6. Chargebacks & Payment Disputes"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "إذا بادرت بطلب استرداد أموال (Chargeback) أو نزاع دفع مع بنكك أو مزود الدفع دون التواصل مع دعم VitesLink أولاً، فإننا نحتفظ بحق:"
									: "If you initiate a chargeback or payment dispute with your bank or payment provider without first contacting VitesLink support, we reserve the right to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>تعليق حسابك أو إنهاءه بشكل دائم.</li>
										<li>الاعتراض على طلب استرداد الأموال بأدلة موثقة على تسليم الخدمة.</li>
										<li>اتخاذ الإجراءات القانونية المناسبة في حالات النزاعات الاحتيالية أو سيئة النية.</li>
									</>
								) : (
									<>
										<li>Suspend or permanently terminate your account.</li>
										<li>Contest the chargeback with documented evidence of service delivery.</li>
										<li>Take appropriate legal action in cases of fraudulent or bad-faith disputes.</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={2}>
								{isAr ? (
									<>
										نشجعك بشدة على التواصل معنا مباشرة عبر <a href="mailto:info@viteslink.com">info@viteslink.com</a>{" "}
										قبل بدء أي نزاع مع مزود الدفع الخاص بك. يمكن حل معظم المشكلات بسرعة عند طرحها مباشرة مع فريقنا.
									</>
								) : (
									<>
										We strongly encourage you to contact us directly at{" "}
										<a href="mailto:info@viteslink.com">info@viteslink.com</a> before initiating any dispute with your
										payment provider. Most issues can be resolved quickly when raised directly with our team.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 7 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "7. منع إساءة الاستخدام" : "7. Abuse Prevention"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تراقب VitesLink أنماط طلبات الاسترداد لرصد إساءة الاستخدام. قد تُلغى أهلية الاسترداد وتُعلَّق الحسابات التي تطلب الاسترداد بشكل متكرر بعد استخدام الخدمة، أو التي تُظهر أنماطًا تتوافق مع إساءة استخدام هذه السياسة، دون إشعار مسبق."
									: "VitesLink monitors refund patterns to detect misuse. Accounts that repeatedly request refunds after using the service, or that show patterns consistent with abuse of this policy, may have their refund eligibility revoked and their accounts suspended without notice."}
							</Typography>
						</Box>

						{/* Section 8 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "8. تعديلات هذه السياسة" : "8. Changes to This Policy"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تحتفظ VitesLink بحق تحديث هذه السياسة في أي وقت. تسري التعديلات فور نشرها على هذه الصفحة. استمرارك في استخدام المنصة بعد أي تحديث يُعد موافقة منك على السياسة المعدّلة."
									: "VitesLink reserves the right to update this policy at any time. Changes take effect immediately upon posting to this page. Continued use of the platform after any update constitutes your acceptance of the revised policy."}
							</Typography>
						</Box>

						{/* Section 9 - Contact */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "9. تواصل معنا" : "9. Contact Us"}</Typography>
							<Typography className={"section-body"} mb={1}>
								{isAr
									? "لطلبات الاسترداد أو استفسارات الإلغاء أو أي أسئلة حول هذه السياسة:"
									: "For refund requests, cancellation queries, or any questions about this policy:"}
							</Typography>
							<Box className={"contact-box"}>
								<Typography fontSize={"14px"} color={"#555"}>
									{isAr ? "البريد الإلكتروني:" : "Email:"} <a href="mailto:info@viteslink.com">info@viteslink.com</a>
								</Typography>
							</Box>
						</Box>
					</Box>
				</Container>
			</RefundPolicySection>
		</Box>
	);
};

export default RefundPolicy;
